export const AGENT_COLOR_KEYS = ['amber', 'rose', 'blue', 'violet', 'yellow', 'teal', 'red'];

// Each color provides:
//   bg        – ultra-light card tint (light mode)
//   fg        – readable text color on bg
//   core      – mid-saturation hue used as the orb core
//   deep      – darker edge color for the orb gradient
//   highlight – soft top-left highlight color
//   glow      – outer glow used for the orb drop shadow
//   dark      – theme-aware tokens for dark mode surfaces
export const AGENT_COLOR_PALETTE = {
  amber: {
    bg: '#FFF3E8',
    fg: '#C2410C',
    core: '#FB923C',
    deep: '#B45309',
    highlight: '#FFE5C2',
    glow: 'rgba(251, 146, 60, 0.35)',
    dark: {
      bg: 'rgba(251, 146, 60, 0.12)',
      cardTint: 'rgba(251, 146, 60, 0.16)',
      fg: '#FDBA74',
      highlight: 'rgba(251, 146, 60, 0.24)',
      glow: 'rgba(251, 146, 60, 0.35)',
    },
  },
  rose: {
    bg: '#FFE9EE',
    fg: '#BE185D',
    core: '#FB7185',
    deep: '#9F1239',
    highlight: '#FFD6DE',
    glow: 'rgba(251, 113, 133, 0.35)',
    dark: {
      bg: 'rgba(251, 113, 133, 0.12)',
      cardTint: 'rgba(251, 113, 133, 0.16)',
      fg: '#FDA4AF',
      highlight: 'rgba(251, 113, 133, 0.24)',
      glow: 'rgba(251, 113, 133, 0.35)',
    },
  },
  blue: {
    bg: '#E6F0FF',
    fg: '#1D4ED8',
    core: '#3B82F6',
    deep: '#1E3A8A',
    highlight: '#C7DBFF',
    glow: 'rgba(59, 130, 246, 0.35)',
    dark: {
      bg: 'rgba(59, 130, 246, 0.12)',
      cardTint: 'rgba(59, 130, 246, 0.16)',
      fg: '#93C5FD',
      highlight: 'rgba(59, 130, 246, 0.24)',
      glow: 'rgba(59, 130, 246, 0.35)',
    },
  },
  violet: {
    bg: '#EEE9FE',
    fg: '#6D28D9',
    core: '#8B5CF6',
    deep: '#4C1D95',
    highlight: '#DCD0FF',
    glow: 'rgba(139, 92, 246, 0.35)',
    dark: {
      bg: 'rgba(139, 92, 246, 0.12)',
      cardTint: 'rgba(139, 92, 246, 0.16)',
      fg: '#C4B5FD',
      highlight: 'rgba(139, 92, 246, 0.24)',
      glow: 'rgba(139, 92, 246, 0.35)',
    },
  },
  yellow: {
    bg: '#FFF8DC',
    fg: '#B45309',
    core: '#F5C518',
    deep: '#A16207',
    highlight: '#FFF1A8',
    glow: 'rgba(245, 197, 24, 0.38)',
    dark: {
      bg: 'rgba(245, 197, 24, 0.10)',
      cardTint: 'rgba(245, 197, 24, 0.14)',
      fg: '#FDE047',
      highlight: 'rgba(245, 197, 24, 0.22)',
      glow: 'rgba(245, 197, 24, 0.38)',
    },
  },
  teal: {
    bg: '#DCFAF5',
    fg: '#0F766E',
    core: '#14B8A6',
    deep: '#0F766E',
    highlight: '#B3F0E5',
    glow: 'rgba(20, 184, 166, 0.35)',
    dark: {
      bg: 'rgba(20, 184, 166, 0.12)',
      cardTint: 'rgba(20, 184, 166, 0.16)',
      fg: '#5EEAD4',
      highlight: 'rgba(20, 184, 166, 0.24)',
      glow: 'rgba(20, 184, 166, 0.35)',
    },
  },
  red: {
    bg: '#FFE7E5',
    fg: '#B91C1C',
    core: '#EF4444',
    deep: '#7F1D1D',
    highlight: '#FFCFCB',
    glow: 'rgba(239, 68, 68, 0.38)',
    dark: {
      bg: 'rgba(239, 68, 68, 0.12)',
      cardTint: 'rgba(239, 68, 68, 0.16)',
      fg: '#FCA5A5',
      highlight: 'rgba(239, 68, 68, 0.24)',
      glow: 'rgba(239, 68, 68, 0.38)',
    },
  },
};

export function colorFromString(seed = '') {
  const text = String(seed);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }
  const index = Math.abs(hash) % AGENT_COLOR_KEYS.length;
  return AGENT_COLOR_KEYS[index];
}

export function resolveAgentColor(flow) {
  const key = flow?.color;
  if (key && AGENT_COLOR_PALETTE[key]) {
    return key;
  }
  return colorFromString(flow?._id || flow?.name || '');
}

export function getAgentColorStyle(flow, mode = 'light') {
  const key = resolveAgentColor(flow);
  const palette = AGENT_COLOR_PALETTE[key] || AGENT_COLOR_PALETTE.violet;

  if (mode === 'dark') {
    const { dark, core, deep } = palette;
    return {
      ...palette,
      bg: dark.bg,
      fg: dark.fg,
      highlight: dark.highlight,
      glow: dark.glow,
      cardTint: dark.cardTint,
      core,
      deep,
    };
  }

  return {
    ...palette,
    cardTint: palette.bg,
  };
}

export function getAgentCardGradient(flow, mode = 'light') {
  const { cardTint } = getAgentColorStyle(flow, mode);
  return `linear-gradient(180deg, ${cardTint} 0%, ${cardTint} 40%, transparent 100%)`;
}
